import React from "react";
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid,
  Tooltip, ResponsiveContainer,
} from "recharts";
import { formatCurrency } from "../utils/formatters";

const PrepaymentSection = ({ emiState, chartData, summary, copyLoanToSip }) => {
  const {
    rateType,
    rateSchedule,
    newRateMonth, setNewRateMonth,
    newRateValue, setNewRateValue,
    newPrepayMonth, setNewPrepayMonth,
    newPrepayAmount, setNewPrepayAmount,
    prepayments,
    setPrepayments,
    addFloatingRateChange,
    removeFloatingRatePeriod,
    resetToFixedRate,
    addPrepayment,
    removePrepayment,
  } = emiState;

  return (
    <div className="card">
      <div className="card-accent-bar" />
      <h2 className="section-title gradient-title">⚡ Prepayments &amp; Rates</h2>

      {/* SIP copy buttons */}
      <div style={{ display: "grid", gap: "10px", marginBottom: "24px" }}>
        <button onClick={() => copyLoanToSip("total")} className="btn btn-primary">
          📊 SIP vs Total Amount Payable
          <span style={{ fontSize: "12px", fontWeight: 500, opacity: 0.9, marginLeft: 6 }}>
            {formatCurrency(summary.totalPayable)}
          </span>
        </button>
        <button onClick={() => copyLoanToSip("interest")} className="btn btn-success">
          💰 SIP vs Total Interest Only
          <span style={{ fontSize: "12px", fontWeight: 500, opacity: 0.9, marginLeft: 6 }}>
            {formatCurrency(summary.totalInterest)}
          </span>
        </button>
      </div>

      {/* Floating rate schedule */}
      {rateType === "floating" && (
        <div className="rate-panel">
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12 }}>
            <h3 style={{ margin: 0 }}>Floating Rate Schedule</h3>
            <button className="reset-btn" onClick={resetToFixedRate}>
              Reset to Fixed
            </button>
          </div>

          <div style={{ maxHeight: "130px", overflowY: "auto", marginBottom: 14 }}>
            {rateSchedule.map((rate, index) => (
              <div key={index} className="rate-period-row">
                <div>
                  <span style={{ fontWeight: 600, fontSize: 13 }}>
                    Month {rate.fromMonth} – {rate.toMonth}
                  </span>
                  <span className="rate-value" style={{ marginLeft: 10 }}>
                    {rate.rate.toFixed(2)}%
                  </span>
                </div>
                <button
                  className="rate-period-remove"
                  onClick={() => removeFloatingRatePeriod(index)}
                  aria-label={`Remove rate period ${rate.fromMonth}–${rate.toMonth}`}
                >
                  ×
                </button>
              </div>
            ))}
          </div>

          <div className="rate-add-grid">
            <input
              type="number"
              value={newRateMonth}
              onChange={(e) => setNewRateMonth(e.target.value)}
              placeholder="From Month"
              className="rate-add-input"
              aria-label="From month for rate change"
            />
            <input
              type="number"
              value={newRateValue}
              onChange={(e) => setNewRateValue(e.target.value)}
              placeholder="New Rate %"
              step="0.1"
              className="rate-add-input"
              aria-label="New interest rate"
            />
          </div>
          <button onClick={addFloatingRateChange} className="rate-add-btn">
            + Add Rate Change
          </button>

          {/* Rate chart */}
          <div style={{ marginTop: 16 }}>
            <div style={{ fontSize: 12, fontWeight: 700, color: "var(--primary-hover)", marginBottom: 8, textTransform: "uppercase", letterSpacing: "0.06em" }}>
              Rate Over Time
            </div>
            <ResponsiveContainer width="100%" height={200}>
              <LineChart data={chartData}>
                <CartesianGrid strokeDasharray="3 3" stroke="var(--primary-border)" />
                <XAxis
                  dataKey="month"
                  tick={{ fontSize: 11, fill: "var(--gray-400)" }}
                  label={{ value: "Month", position: "insideBottom", offset: -4, fontSize: 11 }}
                />
                <YAxis
                  domain={["dataMin - 0.5", "dataMax + 0.5"]}
                  tick={{ fontSize: 11, fill: "var(--gray-400)" }}
                />
                <Tooltip
                  formatter={(v) => [`${v.toFixed(2)}%`, "Rate"]}
                  labelFormatter={(m) => `Month ${m}`}
                  contentStyle={{ borderRadius: 8, border: "1px solid var(--border)", fontSize: 12 }}
                />
                <Line
                  type="stepAfter"
                  dataKey="rate"
                  stroke="var(--primary)"
                  strokeWidth={2.5}
                  dot={false}
                  name="Rate %"
                />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>
      )}

      {/* Lumpsum prepayments */}
      <div style={{ marginTop: rateType === "floating" ? 4 : 0 }}>
        <div style={{ fontSize: 13, fontWeight: 700, color: "var(--gray-700)", marginBottom: 10 }}>
          Lumpsum Prepayments
        </div>

        <div className="prepayment-header">
          <span>Month</span>
          <span>Amount (₹)</span>
          <span />
        </div>

        {prepayments.map((prepay, index) => (
          <div key={index} className="prepayment-row">
            <input
              type="number"
              value={prepay.month}
              onChange={(e) => {
                const updated = [...prepayments];
                updated[index] = { ...updated[index], month: parseInt(e.target.value) || 0 };
                setPrepayments(updated);
              }}
              aria-label={`Prepayment ${index + 1} month`}
            />
            <input
              type="number"
              value={prepay.amount}
              onChange={(e) => {
                const updated = [...prepayments];
                updated[index] = { ...updated[index], amount: parseFloat(e.target.value) || 0 };
                setPrepayments(updated);
              }}
              aria-label={`Prepayment ${index + 1} amount`}
            />
            <button
              className="remove-btn"
              onClick={() => removePrepayment(index)}
              aria-label={`Remove prepayment ${index + 1}`}
            >
              ×
            </button>
          </div>
        ))}

        <div className="prepayment-row">
          <input
            type="number"
            value={newPrepayMonth}
            onChange={(e) => setNewPrepayMonth(e.target.value)}
            placeholder="Month"
            aria-label="New prepayment month"
          />
          <input
            type="number"
            value={newPrepayAmount}
            onChange={(e) => setNewPrepayAmount(e.target.value)}
            placeholder="Amount"
            aria-label="New prepayment amount"
          />
          <button className="add-btn" onClick={addPrepayment} aria-label="Add prepayment">
            +
          </button>
        </div>
      </div>
    </div>
  );
};

export default PrepaymentSection;
